import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosConfig';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import '../styles.css';

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState({}); 
  const [currentUserId] = useState(localStorage.getItem('id')); 
  const [workLocations, setWorkLocations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    contact_number: '',
    role: '',
    active_role: '',
    employee_code: '',
    marital_status: '',
    work_location: '',
    designation: '',
    department: '',
    blood_group: '',
    date_of_birth: '',
    work_anniversary: '',
    nationality: '',
    religion: '',
    official_email_id: '',
    personal_email_id: '',
    mailing_address: '',
    permanent_address: '',
    manager: '',
    is_active: true,
    is_staff: false,
    profile_photo: '',
  });
  const [roleError, setRoleError] = useState('');
  const [rolesData, setRolesData] = useState([]);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    axiosInstance.get(`/user/${userId}/`)
      .then(response => {
        setUserData(response.data); 
      })
      .catch(console.error);

    axiosInstance.get(`/user/${currentUserId}/`)
      .then(response => {
        const userData = response.data;
        setCurrentUser({ ...userData, role: userData.role?.name || 'guest' });
      })
      .catch(console.error);
  }, [userId, currentUserId]);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRolesData([res.data.active_role, res.data.assigned_role]);
      })
      .catch(console.error);
  }, []);

  const handleSwitchRole = async () => {
    try {
      await axiosInstance.post('/switch-role/', { role_id: rolesData[1].id });
      navigate('/user-dashboard');
    } catch (err) {
      setRoleError(err.response?.data?.detail || "Failed to switch role");
    }
  };

  useEffect(() => {
    axiosInstance.get('/office-branch/')
      .then(response => {
        setWorkLocations(response.data);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get('/role/')
      .then(response => {
        setRoles(response.data);
      })
      .catch(console.error);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const profilePhotoUrl = userData.profile_photo?.startsWith('http://') 
    ? userData.profile_photo.replace('http://', 'https://') 
    : userData.profile_photo;

  const DetailItem = ({ label, value }) => (
    <div className="detail-item">
      <label>{label}</label>
      <span>{value || 'N/A'}</span>
    </div>
  );

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={currentUser} 
        activeRole={currentUser?.role}
        error={roleError}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout} 
      />
      
      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <h1>User Details</h1>
          <p>View employee profile and information</p>
        </div>

        <div className="user-details-card">
          <div className="profile-header">
            <div className="profile-avatar-large">
              {profilePhotoUrl ? (
                <img src={profilePhotoUrl} alt="Profile" />
              ) : (
                <div className="avatar-placeholder">
                  <span>&#128100;</span>
                </div>
              )}
            </div>
            <div className="profile-header-info">
              <h2>{userData.first_name} {userData.last_name}</h2>
              <p>{userData.designation || 'Employee'}</p>
              <span className="employee-code">{userData.employee_code || 'N/A'}</span>
            </div>
            <div className="profile-actions">
              <button 
                className="action-btn edit-btn"
                onClick={() => navigate(`/update-user/${userId}`)}
              >
                <span>&#9998;</span> Edit Profile
              </button>
              <button 
                className="action-btn back-btn"
                onClick={() => navigate('/employees')}
              >
                <span>&#8592;</span> Back
              </button>
            </div>
          </div>

          <div className="details-sections">
            <div className="details-section">
              <div className="section-header">
                <span className="section-icon">&#128100;</span>
                <h3>Personal Information</h3>
              </div>
              <div className="details-grid">
                <DetailItem label="First Name" value={userData.first_name} />
                <DetailItem label="Middle Name" value={userData.middle_name} />
                <DetailItem label="Last Name" value={userData.last_name} />
                <DetailItem label="Contact Number" value={userData.contact_number} />
                <DetailItem label="Email" value={userData.email} />
                <DetailItem label="Date of Birth" value={userData.date_of_birth} />
                <DetailItem label="Gender" value={userData.gender} />
                <DetailItem label="Blood Group" value={userData.blood_group} />
                <DetailItem label="Marital Status" value={userData.marital_status} />
                <DetailItem label="Nationality" value={userData.nationality} />
                <DetailItem label="Religion" value={userData.religion} />
                <DetailItem label="Mailing Address" value={userData.mailing_address} />
                <DetailItem label="Permanent Address" value={userData.permanent_address} />
              </div>
            </div>

            <div className="details-section">
              <div className="section-header">
                <span className="section-icon">&#127970;</span>
                <h3>Official Information</h3>
              </div>
              <div className="details-grid">
                <DetailItem label="Employee Code" value={userData.employee_code} />
                <DetailItem label="Official Email" value={userData.official_email_id} />
                <DetailItem label="Personal Email" value={userData.personal_email_id} />
                <DetailItem label="Duty Of" value={userData.duty_of} />
                <DetailItem label="ESIC No." value={userData.esic_no} />
                <DetailItem label="UAN No." value={userData.uan_no} />
                <DetailItem label="Department" value={userData.department} />
                <DetailItem label="Designation" value={userData.designation} />
                <DetailItem label="Work Anniversary" value={userData.work_anniversary} />
                <DetailItem label="Work Location" value={userData.work_location_name} />
                <DetailItem label="Role" value={userData.role_name} />
                <DetailItem label="Manager" value={userData.manager_name} />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserDetails;


